import React from "react";
import { PrimaryButton } from "@/components/custom-button";
import { H1, H2, P } from "@/components/typo";
import Err401 from "@/assets/errors/401.svg";
import Err403 from "@/assets/errors/403.svg";
import Err404 from "@/assets/errors/403.svg";
import Err500 from "@/assets/errors/500.svg";

type ErrorContent = {
  [key: number]: {
    title: string;
    subtitle: string;
    message: string;
    buttonLabel: string;
    svg: React.FC<React.SVGProps<SVGSVGElement>>;
  };
};

const errorContent: ErrorContent = {
  401: {
    title: "401",
    subtitle: "No Authorization Found",
    message: "This Page is not publicly authorized.",
    buttonLabel: "Return Back",
    svg: Err401,
  },
  403: {
    title: "403",
    subtitle: "Forbidden",
    message: "You are not allowed here.",
    buttonLabel: "Return Back",
    svg: Err403,
  },
  404: {
    title: "404",
    subtitle: "Page Not Found",
    message:
      "This Page doesn't exist or was removed! We suggest you go back to home.",
    buttonLabel: "Return Home",
    svg: Err404,
  },
  // Without this entry, an unreachable or failing API fell through to the 404
  // default and told the user their data had been deleted.
  500: {
    title: "500",
    subtitle: "Something Went Wrong",
    message: "We couldn't load this right now. Please try again in a moment.",
    buttonLabel: "Return Home",
    svg: Err500,
  },
};

interface ErrorPageProps {
  statusCode?: number;
  customError?: {
    title: string;
    subtitle: string;
    message: string;
    buttonLabel: string;
    svg: React.FC<React.SVGProps<SVGSVGElement>>;
  };
  onClick: () => void;
}

const ErrorPage: React.FC<ErrorPageProps> = ({
  statusCode,
  customError,
  onClick,
}) => {
  const {
    title,
    subtitle,
    message,
    buttonLabel,
    svg: Svg,
  } = customError ||
  (statusCode && errorContent[statusCode]) ||
  errorContent[404];

  return (
    <div className="h-screen w-full flex justify-center items-center p-16">
      <div className="p-16 flex flex-col gap-3 justify-center">
        <H1>{title}</H1>
        <H2 className="text-gray-700">{subtitle}</H2>
        <P className="text-gray-700">{message}</P>
        <PrimaryButton
          className={`my-3 ${
            statusCode === 401
              ? "bg-[#809DF9] hover:bg-indigo-400"
              : statusCode === 403
                ? "bg-[#64FCD9] hover:bg-teal-300 text-foreground"
                : "bg-[#EF4836] hover:bg-red-400"
          }`}
          label={buttonLabel}
          onPress={onClick}
        />
      </div>
      <div className="h-full w-full p-16">
        <Svg className="w-full h-full object-contain" />
      </div>
    </div>
  );
};

export default ErrorPage;
